import React, { FC } from 'react'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ICustomerContact } from 'apps/fleet-planner/types'
import { IFacility, IFacilityAddress } from 'apps/fleet-planner/utils/api'
import { findPrimaryAddress, findPrimaryContact } from 'apps/fleet-planner/utils/facilities'
import formatPhone from 'utils/formatPhone'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import DISPLAY from 'constants/display'

interface IFacilitiesMobileRow {
  facility: IFacility
  onEditRow: (facility: IFacility) => void
}

const useFacilitiesMobileRowStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${brandColors.coolGray1}`,
  },
}))

export const FacilitiesMobileRow: FC<IFacilitiesMobileRow> = ({ facility, onEditRow }) => {
  const { addresses = [], contacts = [], name = '', updatedAt } = facility || {}
  const classes = useFacilitiesMobileRowStyles()

  const primaryAddress = findPrimaryAddress(addresses)
  const primaryContact = findPrimaryContact(contacts)

  const rowClickHandler = () => {
    onEditRow(facility)
  }

  const parseFacilityAddress = (currentFacility?: IFacilityAddress): JSX.Element | string => {
    const { addressLine1 = '', addressLine2 = '' } = currentFacility || {}
    if (!addressLine1 && !addressLine2) return DISPLAY.empty
    return (
      <Box>
        {addressLine1 && <Box>{addressLine1}</Box>}
        {addressLine2 && <Box>{addressLine2}</Box>}
      </Box>
    )
  }

  const parseFacilityCity = (currentFacility?: IFacilityAddress): JSX.Element | string => {
    const { locality = '', subdivisionCode = '' } = currentFacility || {}
    if (!locality && !subdivisionCode) return DISPLAY.empty
    return (
      <span>
        {locality}
        {locality && subdivisionCode && ', '}
        {subdivisionCode}
      </span>
    )
  }

  const parsePrimaryContact = (currentContact?: ICustomerContact): JSX.Element | string => {
    const { email = '', name: customerName = '', phone = '' } = currentContact || {}
    if (!customerName && !phone && !email)
      return <Box className='body-bold text-primary'>No contact info found.</Box>
    return (
      <Box>
        {customerName && <Box className='b'>{customerName}</Box>}
        {phone && <Box className='fs-mask'>{formatPhone(phone)}</Box>}
        {email && <Box className='fs-mask'>{email}</Box>}
      </Box>
    )
  }

  return (
    <Box className={classes.root} onClick={rowClickHandler} data-test='facilities-mobile-row'>
      <Typography variant='subtitle2'>{name}</Typography>
      <Box>{parseFacilityAddress(primaryAddress)}</Box>
      <Box mb={2}>{parseFacilityCity(primaryAddress)}</Box>
      <Box>{parsePrimaryContact(primaryContact)}</Box>
      <Box mt={2}>
        <span className='body-bold'>Last modified at </span>
        <FormattedDateTime
          datetime={updatedAt || ''}
          format='timeWeekdayDateAndYear'
          showTimezone
          displayTimezone={moment.tz.guess() as AllowedTimezones}
        />
      </Box>
    </Box>
  )
}
